import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sparkles } from "lucide-react";
import api from "../lib/api";
import { TopBar } from "../components/TopBar";
import { useAuth } from "../context/AuthContext";

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const subscribe = async (plan) => {
    const lookup = annual ? plan.lookup_yearly : plan.lookup_monthly;
    if (!lookup) { toast.info("You're on the free plan"); return; }
    setLoadingKey(lookup);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin, user_id: user?.user_id });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <p className="text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3">Billing</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Choose your plan</h1>
          <p className="text-slate-400 mt-3">You're currently on the <span className="text-white capitalize font-medium">{user?.plan || "free"}</span> plan.</p>
          <div className="inline-flex items-center gap-2 mt-6 glass rounded-full p-1">
            <button data-testid="billing-monthly" onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${!annual ? "bg-indigo-600" : "text-slate-400"}`}>Monthly</button>
            <button data-testid="billing-annual" onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${annual ? "bg-indigo-600" : "text-slate-400"}`}>Annual <span className="text-emerald-400">-20%</span></button>
          </div>
        </div>

        {user?.plan === "unlimited" && (
          <div data-testid="owner-unlimited-banner" className="max-w-3xl mx-auto mb-10 glass rounded-2xl p-6 border-indigo-500/40 glow-sm flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shrink-0">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold">Unlimited · Owner access</h3>
              <p className="text-slate-400 text-sm">Your account has unlimited projects, AI usage and every feature unlocked. No billing required.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => {
            const price = annual ? Math.round(p.price_yearly / 12) : p.price_monthly;
            const current = user?.plan === p.id;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`glass rounded-2xl p-6 card-hover relative flex flex-col ${p.highlight ? "border-indigo-500/50 glow-sm" : ""}`}>
                {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-mono px-3 py-1 rounded-full bg-indigo-600 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Popular</span>}
                <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
                <div className="mt-4 mb-5"><span className="font-heading text-4xl font-extrabold">${price}</span><span className="text-slate-500 text-sm">/mo</span></div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
                <button data-testid={`subscribe-${p.id}`} disabled={current || loadingKey} onClick={() => subscribe(p)}
                  className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${current ? "bg-white/[0.04] text-slate-500 cursor-default" : p.highlight ? "bg-indigo-600 hover:bg-indigo-500" : "bg-white/[0.05] hover:bg-white/[0.08]"}`}>
                  {loadingKey === (annual ? p.lookup_yearly : p.lookup_monthly) ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {current ? "Current plan" : p.id === "free" ? "Free forever" : "Upgrade"}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs font-mono text-slate-500 mt-8">Test mode · use card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>
    </div>
  );
}
